import React, { FC, Fragment, useState, ChangeEvent, useContext } from 'react';
import { Box, FormControl, TextField, InputLabel, Select, MenuItem, Alert, Typography, Grid } from '@mui/material';
import { useQuery, useMutation } from '@apollo/client';
import { labClientTemplate } from 'apollo/templates/client-directory';
import { updateTransportAndDeliverySettingsTemplate } from 'apollo/templates/client-directory';
import { LabClient } from 'models/client-directory';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import Loader from 'components/shared/loader/loader.component';
import AlertSnackbar from 'components/shared/alert-snackbar/alert-snackbar.component';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import { customerSettings } from 'utils/customerSettings';
import { CONSTANTS } from 'config/constants';
import { Organization, TransportPartner } from 'models/organization';
import { TransportChannel } from 'models/transport';
import { LoadingButton } from '@mui/lab';
import { AppToasterContext } from 'components/shared/app-toaster/app-toaster-context';

const TransportAndDelivery: FC = () => {
    const { t: translate } = useTranslation(['client-directory', 'common', 'transport-providers']);
    const { id } = useParams<{ id: string }>();
    const [labClient, setLabClient] = useState<LabClient>({} as LabClient);
    const [deliveryInstructions, setDeliveryInstructions] = useState<string>('');
    const [internalDeliveryDaysAdvance, setInternalDeliveryDaysAdvance] = useState<string>('');
    const [externalDeliveryDaysAdvance, setExternalDeliveryDaysAdvance] = useState<string>('');
    const [selectedTransportProvider, setSelectedTransportProvider] = useState<string>('');
    const [changesSaved, setChangesSaved] = useState(false);
    const [showClinicPhone, setShowClinicPhone] = useState<boolean>(false);
    const [clinicPhone, setClinicPhone] = useState<string>('');
    const { showBoundary } = useDenthubErrorBoundary();
    const { setAppToasterContent } = useContext(AppToasterContext);

    const currentOrg = currentOrgVar();
    const isExternalLabUsersEnabled = customerSettings.getCustomerSetting(
        currentOrg.customerSettings,
        CONSTANTS.SETTINGS_NAMES.EXTERNAL_LAB_USERS,
    );
    const transportProviders = currentOrg.transportSettings;

    const { loading: labClientLoading } = useQuery(labClientTemplate, {
        notifyOnNetworkStatusChange: true,
        variables: {
            clientId: id,
        },
        onCompleted: ({ labClient }: { labClient: LabClient }) => {
            setLabClient(labClient);
            setInternalDeliveryDaysAdvance(String(labClient?.internalDeliveryDaysAdvance || 0));
            setExternalDeliveryDaysAdvance(String(labClient?.externalDeliveryDaysAdvance || 0));
            setDeliveryInstructions(labClient.deliveryInstructions || '');
            setClinicPhone(
                labClient.clinicTransportPhone ||
                    (labClient.customer as Organization)?.contact?.communication?.phone ||
                    '',
            );
            if (transportProviders && labClient.transportProvider) {
                if (labClient.transportProvider.channel === TransportChannel.NSHIFT) {
                    setSelectedTransportProvider(
                        `${labClient.transportProvider.channel}_${labClient.transportProvider.externalId}`,
                    );
                    setShowClinicPhone(true);
                } else {
                    setSelectedTransportProvider(labClient.transportProvider.channel);
                }
            } else {
                setSelectedTransportProvider('NONE');
            }
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const [updateSettings, { loading, error }] = useMutation(updateTransportAndDeliverySettingsTemplate, {
        onCompleted: ({ updateTransportAndDeliverySettings: updatedClient }) => {
            setLabClient(Object.assign({ ...labClient, ...updatedClient }));
            setChangesSaved(true);
            setAppToasterContent({
                isOpen: true,
                severity: 'success',
                message: translate('common:CHANGES_SAVED'),
            });
        },
        onError: (err) => {
            console.error(err, 'err');
        },
    });

    const getTransportProvider = () => {
        if (selectedTransportProvider === 'DENTHUB_TRANSPORT') {
            return {
                channel: selectedTransportProvider,
                externalId: transportProviders?.denthubTransport?.externalId,
            };
        }
        if (selectedTransportProvider.match(/nshift_/i)) {
            const [channel, ...partnerNameParts] = selectedTransportProvider.split('_');
            return {
                channel,
                externalId: partnerNameParts.join('_'),
            };
        }
        return null;
    };

    const handleClick = () => {
        const advanceWorkingDays = parseInt(internalDeliveryDaysAdvance || '0');
        const externalDeliveryWorkingDays = parseInt(externalDeliveryDaysAdvance || '0');
        const transportProvider = getTransportProvider();
        updateSettings({
            variables: {
                customerId: id,
                internalDeliveryDaysAdvance: advanceWorkingDays,
                externalDeliveryDaysAdvance: externalDeliveryWorkingDays,
                deliveryInstructions: deliveryInstructions,
                transportProvider,
                clinicTransportPhone: clinicPhone,
            },
        });
    };

    const handleChangeTransportProvider = (newProvider: string | number) => {
        if (typeof newProvider === 'string') {
            setSelectedTransportProvider(newProvider);
            setShowClinicPhone(newProvider.includes('NSHIFT'));
        }
    };

    if (labClientLoading) {
        return <Loader />;
    }

    return (
        <Fragment>
            {changesSaved ? <AlertSnackbar type="success" message={translate('common:CHANGES_SAVED')} /> : undefined}
            {error ? <AlertSnackbar type="error" message={error?.message} /> : undefined}
            <Typography mb={2} variant="h2">
                {translate('client-directory:TRANSPORT_AND_DELIVERY')}
            </Typography>
            {(labClient?.customer as Organization)?.isNotPickUpAndDeliveryPlace ? (
                <Box mt={2}>
                    <Alert severity="info" sx={{ whiteSpace: 'pre-wrap' }}>
                        {translate('client-directory:NOT_PICKUP_OR_DELIVERY_PLACE')}
                    </Alert>
                </Box>
            ) : (
                <Box sx={{ maxWidth: 900 }}>
                    <Grid container spacing={2}>
                        {transportProviders ? (
                            <Fragment>
                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth>
                                        <InputLabel id="transport-provider-label">
                                            {translate('client-directory:TRANSPORT_PROVIDER')}
                                        </InputLabel>
                                        <Select
                                            labelId="transport-provider-label"
                                            value={selectedTransportProvider}
                                            label={translate('client-directory:TRANSPORT_PROVIDER')}
                                            onChange={(e) => handleChangeTransportProvider(e.target.value)}
                                        >
                                            <MenuItem value="NONE">{translate('transport-providers:NONE')}</MenuItem>
                                            {transportProviders.denthubTransport?.isEnabled ? (
                                                <MenuItem value="DENTHUB_TRANSPORT">
                                                    {translate(
                                                        `transport-providers:DENTHUB_TRANSPORT_${transportProviders?.denthubTransport?.externalId}`,
                                                    )}
                                                </MenuItem>
                                            ) : undefined}
                                            {transportProviders.nShiftTransport?.isEnabled
                                                ? transportProviders.nShiftTransport?.partners?.map(
                                                      (partner: TransportPartner, index: number) => {
                                                          return (
                                                              <MenuItem key={index} value={`NSHIFT_${partner.id}`}>
                                                                  {translate(
                                                                      `transport-providers:NSHIFT_${partner.id}`,
                                                                  )}
                                                              </MenuItem>
                                                          );
                                                      },
                                                  )
                                                : undefined}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    {showClinicPhone ? (
                                        <FormControl fullWidth required>
                                            <TextField
                                                required
                                                helperText={translate('client-directory:TRANSPORT_CLINIC_PHONE_NUMBER')}
                                                label={translate('common:PHONE')}
                                                value={clinicPhone}
                                                onChange={(e) => {
                                                    setClinicPhone(e.target.value);
                                                }}
                                                error={showClinicPhone && !clinicPhone}
                                            />
                                        </FormControl>
                                    ) : undefined}
                                </Grid>
                                <Grid item xs={12} sm={12}>
                                    <FormControl fullWidth>
                                        <TextField
                                            label={translate('client-directory:DELIVERY_INSTRUCTIONS')}
                                            value={deliveryInstructions}
                                            onInput={(e: ChangeEvent<HTMLInputElement>) => {
                                                setDeliveryInstructions(e.target.value);
                                            }}
                                        />
                                    </FormControl>
                                </Grid>
                            </Fragment>
                        ) : undefined}

                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <TextField
                                    helperText={translate(
                                        'client-directory:INTERNAL_DELIVERY_WORKING_DAYS_HELPER_TEXT',
                                    )}
                                    label={translate('client-directory:INTERNAL_DELIVERY_WORKING_DAYS_IN_ADVANCE')}
                                    value={internalDeliveryDaysAdvance}
                                    onInput={(e: ChangeEvent<HTMLInputElement>) => {
                                        setInternalDeliveryDaysAdvance(e.target.value.replace(/[^0-9]/g, ''));
                                    }}
                                    InputProps={{
                                        inputProps: {
                                            min: 1,
                                        },
                                    }}
                                />
                            </FormControl>
                        </Grid>

                        {isExternalLabUsersEnabled ? (
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth>
                                    <TextField
                                        helperText={translate(
                                            'client-directory:EXTERNAL_DEADLINE_WORKING_DAYS_HELPER_TEXT',
                                        )}
                                        label={translate('client-directory:EXTERNAL_DEADLINE_WORKING_DAYS_IN_ADVANCE')}
                                        value={externalDeliveryDaysAdvance}
                                        onInput={(e: ChangeEvent<HTMLInputElement>) => {
                                            setExternalDeliveryDaysAdvance(e.target.value.replace(/[^0-9]/g, ''));
                                        }}
                                        InputProps={{
                                            inputProps: {
                                                min: 1,
                                            },
                                        }}
                                    />
                                </FormControl>
                            </Grid>
                        ) : undefined}
                    </Grid>
                    <Box mt={2}>
                        <LoadingButton
                            disabled={showClinicPhone && !clinicPhone}
                            variant="contained"
                            type="button"
                            onClick={handleClick}
                            loading={loading}
                        >
                            {translate('common:SAVE')}
                        </LoadingButton>
                    </Box>
                </Box>
            )}
        </Fragment>
    );
};

export default TransportAndDelivery;
